import { useCallback } from 'react';

export interface UseOptionKeysOptions<T extends object> {
  value?: T | null;
  getOptionLabel: (option: T) => string;
  getOptionKey?: (option: T) => string | number;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
}

export interface OptionKeys<T extends object> {
  getKey: (option: T) => string;
  isOptionSelected: (option: T) => boolean;
}

export function useOptionKeys<T extends object>({
  value,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
}: UseOptionKeysOptions<T>): OptionKeys<T> {
  // Generate a key for each option
  const getKey = useCallback(
    (option: T): string => {
      if (getOptionKey) {
        return String(getOptionKey(option));
      }
      // Default: use the label as key
      return getOptionLabel(option);
    },
    [getOptionKey, getOptionLabel]
  );

  // Check if an option matches the current value
  const isOptionSelected = useCallback(
    (option: T): boolean => {
      if (!value) return false;
      if (isOptionEqualToValue) return isOptionEqualToValue(option, value);
      return getKey(option) === getKey(value);
    },
    [value, getKey, isOptionEqualToValue]
  );

  return { getKey, isOptionSelected };
}
